import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Lightning, PlayCircle, PauseCircle, Trash, Users, EnvelopeSimple, Robot, Sparkle } from "@phosphor-icons/react";

const STATUS_COLORS = {
  draft: "var(--text-3)",
  active: "var(--accent)",
  paused: "#FF9500",
  archived: "var(--text-3)",
};

export default function Campaigns() {
  const nav = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [library, setLibrary] = useState([]);

  const load = async () => {
    const [c, l] = await Promise.all([api.get("/automation/campaigns"), api.get("/automation/library")]);
    setCampaigns(c.data.campaigns || []);
    setLibrary(l.data.templates || []);
  };
  useEffect(() => { load(); }, []);

  const createFromLibrary = async (key) => {
    try {
      const r = await api.post("/automation/campaigns/duplicate-from-library", { key });
      toast.success("Campaign draft created");
      nav(`/campaigns/${r.data.campaign_id}`);
    } catch { toast.error("Could not clone template"); }
  };

  const createBlank = async () => {
    try {
      const r = await api.post("/automation/campaigns", {
        name: "Untitled Campaign", status: "draft", trigger_type: "manual",
        steps: [{ step_order: 0, step_type: "email", delay_amount: 0, delay_unit: "days",
                  configuration: { subject: "Hi {{first_name}}", body: "Hello from {{user_first_name}}" } }],
      });
      nav(`/campaigns/${r.data.campaign_id}`);
    } catch { toast.error("Could not create campaign"); }
  };

  const toggleStatus = async (c) => {
    const next = c.status === "active" ? "paused" : "active";
    await api.put(`/automation/campaigns/${c.id}`, { status: next });
    toast.success(next === "active" ? "Campaign activated" : "Campaign paused");
    load();
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Active enrollments will be cancelled.`)) return;
    await api.delete(`/automation/campaigns/${c.id}`);
    load();
  };

  return (
    <div className="p-6 md:p-10" data-testid="campaigns-page">
      <Toaster theme="dark" />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">// automation</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Campaigns</h1>
          <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--text-2)" }}>
            Build multi-step cadences that run on autopilot. Replies auto-pause, unsubscribes auto-exit, and nothing sends outside your quiet hours.
          </p>
        </div>
        <button onClick={createBlank} className="btn-primary flex items-center gap-2" data-testid="new-campaign-btn">
          <Plus weight="bold" /> New campaign
        </button>
      </div>

      <section className="mb-10">
        <div className="overline mb-4">// my campaigns</div>
        {campaigns.length === 0 && (
          <div className="border p-8 text-center" style={{ borderColor: "var(--border)" }} data-testid="campaigns-empty">
            <Robot weight="bold" size={32} style={{ color: "var(--accent)", margin: "0 auto 12px" }} />
            <div className="text-sm" style={{ color: "var(--text-2)" }}>No campaigns yet. Start from a template below or build from scratch.</div>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {campaigns.map((c) => (
            <div key={c.id} className="border p-5" style={{ borderColor: "var(--border)" }} data-testid={`campaign-${c.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <Link to={`/campaigns/${c.id}`} className="font-display font-bold text-xl hover:underline block truncate" data-testid={`campaign-link-${c.id}`}>{c.name}</Link>
                  <div className="overline mt-1">{c.step_count} steps · trigger: {c.trigger_type}</div>
                </div>
                <span className="tag" style={{ color: STATUS_COLORS[c.status], borderColor: STATUS_COLORS[c.status] }}>{c.status}</span>
              </div>
              {c.description && <div className="text-sm mb-3" style={{ color: "var(--text-2)" }}>{c.description}</div>}
              <div className="flex items-center gap-4 text-xs mb-4" style={{ color: "var(--text-3)" }}>
                <span className="flex items-center gap-1"><Users weight="bold" /> {c.active_enrollments} active</span>
                <span className="flex items-center gap-1"><EnvelopeSimple weight="bold" /> {c.completed_enrollments} completed</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => nav(`/campaigns/${c.id}`)} className="btn-secondary text-xs" data-testid={`edit-campaign-${c.id}`}>Edit</button>
                <button onClick={() => toggleStatus(c)} className="btn-secondary text-xs flex items-center gap-1" data-testid={`toggle-campaign-${c.id}`}>
                  {c.status === "active" ? (<><PauseCircle weight="bold" /> Pause</>) : (<><PlayCircle weight="bold" /> Activate</>)}
                </button>
                <button onClick={() => remove(c)} className="btn-secondary text-xs" data-testid={`delete-campaign-${c.id}`}><Trash weight="bold" /></button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="overline mb-4">// start from a template</div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {library.map((t) => (
            <button
              key={t.key}
              onClick={() => createFromLibrary(t.key)}
              className="border p-5 text-left hover:bg-[#0F1E24]"
              style={{ borderColor: "var(--border)" }}
              data-testid={`library-${t.key}`}
            >
              <Sparkle weight="bold" style={{ color: "var(--accent)" }} />
              <div className="font-display font-bold text-xl mt-2">{t.name}</div>
              <div className="text-sm mt-2" style={{ color: "var(--text-2)" }}>{t.description}</div>
              <div className="overline mt-3">{t.steps.length} steps</div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
